import { UserColumn, userColumns } from "@/types/userColumns"
import { Module } from "vuex"
import { BaseState } from "./index"

import { Team, Field, DataDefinition, Rule, DataType, dataTypeByFieldType, CalculationDataTypeValue, CalculationType, FieldType } from "@/types"

import api from "@/services/api"
import { operatorsByFieldType } from "@/types/insights"
import { CaseColumn, caseColumns } from "@/types/caseColumns"

export interface CasesSetupState {
	teams: Team[];
	fieldTypes: FieldType[];
	dataSources: any[] | null;
	definitions: DataDefinition[];
	fields: Field[];
	fieldLoadPromise: Promise<Field[]> | null
	userFields: Field[];
	rules: Rule[];
	adEnabled: boolean | null;
}

export const state: CasesSetupState = {
	teams: [],
	fieldTypes: [
		{ id: "text", title: "Text", description: "Single line of text", type: DataType.string },
		{ id: "text-area", title: "Text Area", description: "Block of text covering multiple lines", type: DataType.string },
		{ id: "number", title: "Number", description: "Numerical value", type: DataType.number },
		{ id: "dollar", title: "Dollar amount", description: "Dollar value", type: DataType.number },
		{ id: "date", title: "Date", description: "Enter a date", type: DataType.dateTime },
		{ id: "date-time", title: "Date and Time", description: "Enter a date and time", type: DataType.dateTime },
		{ id: "checkbox", title: "Checkbox", description: "Box with ability to toggle between checked and unchecked", type: DataType.boolean },
		{ id: "list", title: "Select from list", description: "Select single item from a list", type: DataType.array },
		{ id: "list-multiple", title: "Select multiple from list", description: "Select multiple items from a list", type: DataType.arrayMultiple },
		{ id: "list-data", title: "Data object", description: "Select multiple items from a data source, with ability to add adhoc items", type: DataType.arrayData },
		{ id: "calculated", title: "Calculated Value", description: "Calculate a value based on another field", type: DataType.calculated },
		{ id: "file-only", title: "File Upload", description: "Upload Files", type: DataType.none },
		{ id: "map-marker", title: "Map Markers", description: "Single points on map", type: DataType.mapPoint },
		{ id: "map-path", title: "Map Path", description: "Path between points on map", type: DataType.mapPoint },
		{ id: "map-region", title: "Map Region", description: "Filled region on map", type: DataType.mapPoint },
		{ id: "user-role", title: "User Role", description: "User Role", type: DataType.userRole },
		{ id: "external-link", title: "External Link", description: "A clickable Link that opens up in a new tab", type: DataType.string },
		{ id: "case-number", title: "Case Number", description: "Unique Case Number", type: DataType.string },
		{ id: "icon-upload", title: "Icon Upload", description: "A field for uploading icon files, entering an icon URL or providing a Font Awesome icon", type: DataType.string }
	],
	dataSources: null,
	definitions: [],
	fields: [],
	fieldLoadPromise: null,
	userFields: [],
	rules: [],
	adEnabled: null
}

export const setup: Module<CasesSetupState, BaseState> = {
	namespaced: true,
	state,
	getters: {
		getTeams: state => state.teams,
		getFieldTypes: state => state.fieldTypes,
		getFieldTypesById: state => state.fieldTypes.toDictionary("id"),
		getDataSources: state => state.dataSources,
		getDataSourcesById: state => state.dataSources ? state.dataSources.toDictionary("id") : {},
		getDefinitions: state => state.definitions,
		getDefinitionsById: state => state.definitions ? state.definitions.toDictionary("id") : {},
		getAllFields: state => [
			...caseColumns,
			...(state.fields
				? state.fields.map((f):CaseColumn => {
					return {
						label: f.title,
						description: f.description,
						key: "fields." + f.id,
						fieldId: f.id,
						dataType: f.dataType!,
						definitionId: f.definitionId,
						dataSourceId: f.dataSourceId,
						dynamicField: true,
						fieldExtensions: f.extensions,
						fieldDataType: f.dataType === "calculated"
							? CalculationDataTypeValue[f.extensions!.calculationType as CalculationType]
							: dataTypeByFieldType[f.dataType!]
								? dataTypeByFieldType[f.dataType!]
								: undefined
					}
				})
				: [])
		],
		getFilterableFields: (state, getters, rootState) => { // all the filtering and tweaking that was done to fields VueSetup and Insights has been moved here in one place
			const userIsAdmin = rootState.me ? rootState.me.isAdmin : false

			const fields = getters.getAllFields.filter((f: CaseColumn) => (!f.adminOnly || userIsAdmin) && (!f.dynamicField || operatorsByFieldType[f.dataType!]))

			// Do not display the calculated field type - CalculationType.DaysUntilOrFromToday
			fields.forEach((item: CaseColumn, index: number, array: any) => {
				if (item.label === "days until or from today") {
					array.splice(index, 1)
				}
			})

			return fields
		},
		getAllUserFields: state => [
			...userColumns,
			...(state.userFields
				? state.userFields.map((f):UserColumn => {
					return {
						label: f.title,
						description: f.description,
						key: "fields." + f.id,
						fieldId: f.id,
						dataType: f.dataType!,
						definitionId: f.definitionId,
						dataSourceId: f.dataSourceId,
						dynamicField: true,
						fieldExtensions: f.extensions,
						fieldDataType: dataTypeByFieldType[f.dataType!]
							? dataTypeByFieldType[f.dataType!]
							: undefined
					}
				})
				: [])
		],
		getFilterableUserFields: (state, getters) => { // all the filtering and tweaking that was done to fields VueSetup and Insights has been moved here in one place
			const fields = getters.getAllUserFields.filter((f: UserColumn) => !f.dynamicField || operatorsByFieldType[f.dataType!])

			// Do not display the calculated field type - CalculationType.DaysUntilOrFromToday
			fields.forEach((item: UserColumn, index: number, array: any) => {
				if (item.label === "days until or from today") {
					array.splice(index, 1)
				}
			})
			return fields
		}
	},
	mutations: {
		setTeams: (state, teams: Team[]) => state.teams = teams,
		setDataSources: (state, dataSources: any[]) => state.dataSources = dataSources,
		setDefinitions: (state, definitions: DataDefinition[]) => state.definitions = definitions,
		setFields: (state, fields: Field[]) => state.fields = fields,
		setFieldLoadPromise: (state, fieldLoadPromise: Promise<Field[]>) => state.fieldLoadPromise = fieldLoadPromise,
		setUserFields: (state, userFields: Field[]) => state.userFields = userFields,
		setRules: (state, rules: Rule[]) => state.rules = rules,
		addDataSource: (state, dataSource: any) => {
			if (state.dataSources) {
				state.dataSources.push(dataSource)
			} else {
				state.dataSources = [dataSource]
			}
		},
		setAdEnabled: (state, enabled: boolean) => state.adEnabled = enabled,
		addField: (state, field: Field) => {
			if (state.fields && !state.fields.some(f => f.id === field.id)) {
				state.fields.push(field)
			}
		}
	},
	actions: {
		async loadTeams ({ commit }) {
			commit("setTeams", await api.teamsGet())
		},
		async loadTeamsIfUnloaded ({ state, dispatch }) {
			if (!state.teams || state.teams.length === 0) {
				await dispatch("loadTeams")
			}
		},
		async loadDefinitions ({ commit }) {
			commit("setDefinitions", await api.definitions())
		},
		async loadDataSources ({ commit }) {
			commit("setDataSources", await api.dataSources())
		},
		async loadFields ({ commit, dispatch, state, getters, rootState }) {
			const fields = (await rootState.fieldCache?.fetchIfNeeded()) ?? []

			fields.forEach((field: Field) => {
				field.dataTypeTitle = getters.getFieldTypesById[field.dataType!] ? getters.getFieldTypesById[field.dataType!].title : ""

				if (field.dataSourceId && getters.getDataSourcesById[field.dataSourceId]) {
					field.dataSourceTitle = getters.getDataSourcesById[field.dataSourceId].title
				}
				if (field.definitionId && getters.getDefinitionsById[field.definitionId]) {
					field.definitionTitle = getters.getDefinitionsById[field.definitionId].title
				}
			})

			commit("setFields", fields)
			dispatch("loadUserFieldsIfUnloaded")

			return fields
		},
		async loadFieldsIfUnloaded ({ dispatch, state, commit, rootState }) {
			const hasFieldCacheFetchedData = !!rootState.fieldCache?.hasFetchedData
			if (state.fields.length === 0 || !hasFieldCacheFetchedData) {
				// Ensure request for fields is only made once
				if (state.fieldLoadPromise) {
					await state.fieldLoadPromise
				} else {
					const fieldLoadPromise = dispatch("loadFields")
					commit("setFieldLoadPromise", fieldLoadPromise)
					await fieldLoadPromise
					commit("setFieldLoadPromise", null)
				}
			}
			return state.fields
		},
		async loadUserFieldsIfUnloaded ({ dispatch, state, commit }) {
			if (state.userFields.length === 0) {
				if (state.fields.length === 0) {
					await dispatch("loadFieldsIfUnloaded")
				} else {
					const userDefinition = (await api.userSpecialDefinition())
					const userFieldIds = userDefinition ? userDefinition.fields.map(c => c.field.id!) : []
					const userFields = state.fields.filter(c => userFieldIds.includes(c.id!))

					commit("setUserFields", userFields)
				}
			}
		},
		async loadRules ({ commit }) {
			const rules = await api.rules()
			commit("setRules", rules)

			return rules
		},
		async definitionGetFromSource ({ getters, commit }, dataSourceId: string) {
			const storeDataSources = getters.getDataSources || []
			let source = storeDataSources.find((d: any) => d.id === dataSourceId)
			if (!source) {
				source = await api.dataSourceGet(dataSourceId)
				commit("addDataSource", source)
			}

			return api.definitionDetails(source.definitionId)
		},
		async getADEnabled ({ state, commit }) {
			if (state.adEnabled !== null) {
				return state.adEnabled
			}

			const adEnabled = await api.adEnabled()
			if (adEnabled !== undefined) {
				commit("setAdEnabled", adEnabled)
			}

			return adEnabled
		}
	}
}
